"use client";

import { TypeProgram_metaWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import { parseMetaTemplate } from "@2u-landing-pages/utils/parseProgramMeta";
import React, { createContext, useContext, useMemo } from "react";

type ProgramMeta = TypeProgram_metaWithoutUnresolvableLinksResponse | null;

const ProgramMetaContext = createContext<ProgramMeta>(null);

/**
 * Scoped provider for the (static) Program Meta entry. Placed at the program
 * subtree root and re-provided wherever a different program applies (e.g. each
 * ProductCard on a multi-program page).
 */
export const ProgramMetaProvider: React.FC<{
  children: React.ReactNode;
  programMeta?: TypeProgram_metaWithoutUnresolvableLinksResponse;
}> = ({ children, programMeta }) => {
  const value = useMemo<ProgramMeta>(() => programMeta ?? null, [programMeta]);

  return <ProgramMetaContext.Provider value={value}>{children}</ProgramMetaContext.Provider>;
};

/** Returns the full Program Meta entry for the current subtree (or null). */
export const useProgramMeta = (): ProgramMeta => useContext(ProgramMetaContext);

/** Returns just the `fields` of the current Program Meta entry. */
export const useProgramMetaFields = () => useContext(ProgramMetaContext)?.fields;

/** Selects a derived value from Program Meta fields, e.g. `(f) => f?.tuition_cost`. */
export const useProgramMetaField = <T,>(selector: (fields?: ProgramMeta extends null ? undefined : NonNullable<ProgramMeta>["fields"]) => T): T => {
  const meta = useContext(ProgramMetaContext);
  return selector(meta?.fields as any);
};

/** Returns a memoized parser for `{{meta.*}}` tokens bound to the current entry. */
export const useParseMeta = () => {
  const meta = useContext(ProgramMetaContext);
  return useMemo(() => (template: string) => parseMetaTemplate(template, meta), [meta]);
};
